import PartnerLogo from "@components/Location/PartnerLogo";
import {useTranslation} from "@i18n/client";
import React, {useState} from "react";
import {formatPhoneNumber, useEffectAsync} from "src/components/_common/Carbon";
import {RootStateLocation} from "src/store/types";
import {analytics} from "src/utils/analytics";

import {PartnerLogoType} from "../../../constants/LocationConstants";
import {getGeolocation} from "../../../utils/browser-storage/location";
import {wholeMilesBetweenCoordinates} from "../../../utils/distance";

type Props = {
  location: RootStateLocation;
  logo?: PartnerLogoType;
  /**
   * `metadata.h1` is used to override the location name in the header when present.
   */
  h1?: string;
};

const Details: React.FC<Props> = ({location, logo, h1}) => {
  const [dist, setDist] = useState("");
  const i18n = useTranslation();
  const {
    address: {firstLine, zip, latitude, longitude},
    yelpRating,
    phoneNumber,
  } = location;

  useEffectAsync(async () => {
    const userLocation = getGeolocation();
    if (userLocation) {
      const newDist = wholeMilesBetweenCoordinates(
        latitude,
        longitude,
        userLocation.x,
        userLocation.y,
      );
      setDist(`${newDist} mi`);
    }
  }, [latitude, longitude]);

  const onViewMap = () => {
    analytics.post({
      category: "LocationPageInfo",
      label: "ClinicMap",
      action: "Clicked",
      extraData: {
        number: `${firstLine}, ${zip}`,
        distance: dist,
      },
    });
  };

  const onClickPhoneNumber = () => {
    analytics.post({
      category: "LocationPageInfo",
      label: "ClinicPhoneNumber",
      action: "Clicked",
      extraData: {
        number: phoneNumber,
      },
    });
  };

  return (
    <div className="ph1 pt2 pb2">
      <div className="df fdc fs14 font-ir">
        <h1 className="fs24 font-csb ls-3 black">
          {h1 || location.name}
          <span className="db mb2">
            <PartnerLogo logo={logo} />
          </span>
        </h1>
        <span className="gray600 lh14 mb2">
          <span>
            {firstLine}, {zip}
            <span aria-hidden> · </span>
          </span>
          <span>
            {dist}
            <span aria-hidden> · </span>
          </span>
          <a href="#directions" className="darkerBlue font-isb wsnw" onClick={onViewMap}>
            {i18n.t("View Map")}
          </a>
        </span>
        <span>
          {yelpRating && (
            <span>
              <span className="cIcon-star darkYellow mr1 fs12" aria-hidden />
              {yelpRating}
              <span className="visually-hidden"> {i18n.t("stars")}</span>
              <span aria-hidden> · </span>
            </span>
          )}
          <a
            href={`tel:${phoneNumber}`}
            className="darkerBlue font-isb wsnw"
            onClick={onClickPhoneNumber}
          >
            {/* @ts-expect-error TS2345: Argument of type 'string | undefined' is not assignable to parameter of type 'string'. */}
            {formatPhoneNumber(phoneNumber)}
          </a>
        </span>
      </div>
    </div>
  );
};

export default React.memo(Details);
